import { Edit, Copy, Share2, Archive, Trash2, MoreVertical, Settings, Download, FileText, RefreshCw, Eye, Ban, CheckCircle } from 'lucide-react';
import { DropdownMenu } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Shared no-op handler for static demo menu items
const staticSelect = () => {};

function DropdownMenuShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="dropdown alignment and side">
          <DropdownMenu>
            <DropdownMenu.Trigger>Actions</DropdownMenu.Trigger>
            <DropdownMenu.Content
              align={playgroundProps.align as 'start' | 'center' | 'end'}
              side={playgroundProps.side as 'top' | 'bottom'}
            >
              <DropdownMenu.Item icon={<Icon icon={Eye} size="sm" />} onSelect={staticSelect}>View Policy Details</DropdownMenu.Item>
              <DropdownMenu.Item icon={<Icon icon={Edit} size="sm" />} onSelect={staticSelect}>Edit Coverage Plan</DropdownMenu.Item>
              <DropdownMenu.Item icon={<Icon icon={RefreshCw} size="sm" />} onSelect={staticSelect}>Renew Policy</DropdownMenu.Item>
              <DropdownMenu.Divider />
              <DropdownMenu.Item icon={<Icon icon={Trash2} size="sm" />} destructive onSelect={staticSelect}>Cancel Policy</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
      </ShowcasePreviewSection>

      {/* Basic Menu */}
      <ShowcaseDemoSection
        title="Basic Menu"
        description="Simple dropdown with text items for common actions"
       
      >
          <DropdownMenu>
            <DropdownMenu.Trigger>Claim Options</DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item onSelect={staticSelect}>View Claim</DropdownMenu.Item>
              <DropdownMenu.Item onSelect={staticSelect}>Add Document</DropdownMenu.Item>
              <DropdownMenu.Item onSelect={staticSelect}>Request Review</DropdownMenu.Item>
              <DropdownMenu.Item onSelect={staticSelect}>Export Details</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
      </ShowcaseDemoSection>

      {/* Alignment Options */}
      <ShowcaseDemoSection
        title="Alignment Options"
        description="Control horizontal alignment relative to the trigger button"
       
      >
          <HStack gap={8} justify="between" className="w-full">
            <VStack gap={2}>
              <Text size="sm" color="secondary">Start (default)</Text>
              <DropdownMenu>
                <DropdownMenu.Trigger>Align Start</DropdownMenu.Trigger>
                <DropdownMenu.Content align="start">
                  <DropdownMenu.Item onSelect={staticSelect}>Option 1</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 2</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 3</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </VStack>

            <VStack gap={2}>
              <Text size="sm" color="secondary">Center</Text>
              <DropdownMenu>
                <DropdownMenu.Trigger>Align Center</DropdownMenu.Trigger>
                <DropdownMenu.Content align="center">
                  <DropdownMenu.Item onSelect={staticSelect}>Option 1</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 2</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 3</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </VStack>

            <VStack gap={2}>
              <Text size="sm" color="secondary">End</Text>
              <DropdownMenu>
                <DropdownMenu.Trigger>Align End</DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onSelect={staticSelect}>Option 1</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 2</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={staticSelect}>Option 3</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Menu items with icons for improved visual recognition"
       
      >
          <HStack gap={4}>
            <DropdownMenu>
              <DropdownMenu.Trigger>Document Actions</DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item icon={<Icon icon={Eye} size="sm" />} onSelect={staticSelect}>
                  View Document
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={Download} size="sm" />} onSelect={staticSelect}>
                  Download
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={Share2} size="sm" />} onSelect={staticSelect}>
                  Share
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={Copy} size="sm" />} onSelect={staticSelect}>
                  Duplicate
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenu.Trigger>Settings</DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item icon={<Icon icon={Settings} size="sm" />} onSelect={staticSelect}>
                  Preferences
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={RefreshCw} size="sm" />} onSelect={staticSelect}>
                  Sync Data
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={FileText} size="sm" />} onSelect={staticSelect}>
                  View Logs
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </HStack>
      </ShowcaseDemoSection>

      {/* With Dividers */}
      <ShowcaseDemoSection
        title="With Dividers"
        description="Group related actions using dividers for visual separation"
       
      >
          <DropdownMenu>
            <DropdownMenu.Trigger>Policy Management</DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {([
                { lucideIcon: Eye, text: 'View Policy' },
                { lucideIcon: Edit, text: 'Edit Details' },
                { type: 'divider' as const, id: 'sep-edit-renew' },
                { lucideIcon: RefreshCw, text: 'Renew Coverage' },
                { lucideIcon: Copy, text: 'Duplicate Policy' },
                { type: 'divider' as const, id: 'sep-duplicate-archive' },
                { lucideIcon: Archive, text: 'Archive' },
                { lucideIcon: Trash2, text: 'Cancel Policy', isDestructive: true },
              ]).map((entry) =>
                'type' in entry ? (
                  <DropdownMenu.Divider key={entry.id} />
                ) : (
                  <DropdownMenu.Item
                    key={entry.text}
                    icon={<Icon icon={entry.lucideIcon} size="sm" />}
                    destructive={entry.isDestructive}
                    onSelect={staticSelect}
                  >
                    {entry.text}
                  </DropdownMenu.Item>
                ),
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
      </ShowcaseDemoSection>

      {/* Disabled Items */}
      <ShowcaseDemoSection
        title="Disabled Items"
        description="Individual items can be disabled based on permissions or state"
       
      >
          <DropdownMenu>
            <DropdownMenu.Trigger>Claim Actions</DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {([
                { lucideIcon: Eye, text: 'View Claim', isDisabled: false },
                { lucideIcon: Edit, text: 'Edit Claim (Locked)', isDisabled: true },
                { lucideIcon: CheckCircle, text: 'Approve (Pending Review)', isDisabled: true },
                { type: 'divider' as const, id: 'sep-approve-export' },
                { lucideIcon: Download, text: 'Export Report', isDisabled: false },
                { lucideIcon: Ban, text: 'Void Claim (No Permission)', isDisabled: true, isDestructive: true },
              ]).map((entry) =>
                'type' in entry ? (
                  <DropdownMenu.Divider key={entry.id} />
                ) : (
                  <DropdownMenu.Item
                    key={entry.text}
                    icon={<Icon icon={entry.lucideIcon} size="sm" />}
                    disabled={entry.isDisabled}
                    destructive={entry.isDestructive}
                    onSelect={staticSelect}
                  >
                    {entry.text}
                  </DropdownMenu.Item>
                ),
              )}
            </DropdownMenu.Content>
          </DropdownMenu>
      </ShowcaseDemoSection>

      {/* Destructive Actions */}
      <ShowcaseDemoSection
        title="Destructive Actions"
        description="Highlight dangerous actions with destructive styling (red color)"
       
      >
          <HStack gap={4}>
            <DropdownMenu>
              <DropdownMenu.Trigger>
                <Icon icon={MoreVertical} size="sm" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item icon={<Icon icon={Edit} size="sm" />} onSelect={staticSelect}>
                  Edit Coverage
                </DropdownMenu.Item>
                <DropdownMenu.Item icon={<Icon icon={Archive} size="sm" />} onSelect={staticSelect}>
                  Archive Policy
                </DropdownMenu.Item>
                <DropdownMenu.Divider />
                <DropdownMenu.Item icon={<Icon icon={Trash2} size="sm" />} destructive onSelect={staticSelect}>
                  Delete Policy
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenu.Trigger>Beneficiary Options</DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item onSelect={staticSelect}>View Details</DropdownMenu.Item>
                <DropdownMenu.Item onSelect={staticSelect}>Update Information</DropdownMenu.Item>
                <DropdownMenu.Item onSelect={staticSelect}>Add Dependent</DropdownMenu.Item>
                <DropdownMenu.Divider />
                <DropdownMenu.Item destructive onSelect={staticSelect}>
                  Remove Beneficiary
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const DropdownMenuShowcase: ShowcaseDefinition = {
  id: 'dropdown-menu',
  name: 'DropdownMenu',
  category: 'actions',
  description: 'Contextual menu triggered by a button click',
  propsDefinition: [
    {
      name: 'align',
      type: 'select',
      options: ['start', 'center', 'end'],
      defaultValue: 'start',
      description: 'Horizontal alignment relative to trigger',
    },
    {
      name: 'side',
      type: 'select',
      options: ['top', 'bottom'],
      defaultValue: 'bottom',
      description: 'Side to open the dropdown',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'aria-haspopup="menu"',
      'aria-expanded (on trigger)',
      'aria-controls (points to menu)',
      'role="menu" (on content)',
      'role="menuitem" (on items)',
    ],
    keyboardNav: [
      'Enter/Space: Open menu, select item',
      'Arrow Down/Up: Navigate items',
      'Escape: Close menu',
      'Home/End: Jump to first/last item',
      'Tab: Close menu and move focus',
    ],
    notes: [
      'Full keyboard navigation support',
      'Click outside to close',
      'Proper focus management on open/close',
      'Disabled items are not focusable',
    ],
  },
  component: DropdownMenuShowcaseComponent,
};
